import type { DiscoveryPreview, SuburbInput } from "@repo/adapter-core";
import { slugify } from "@repo/adapter-core";
import type { SoldOnlyAdapterConfig } from "../shared/sold-only.ts";

// Buxton's sold search page (/sold-properties/in-<suburb>…/) renders only the 12 newest sales and
// ignores ?page=N; "Load more" reads cms.buxton.com.au/api/v1/listings/ client-side. Discovery
// reads that API instead (measured 2026-10-14): `suburb` + `postcode` filter it to one suburb,
// results are newest sale first, `limit`/`offset` page through every sale (Brighton: ~1,950, back
// to ~2010), and each record carries the sale date and price. A page past the end is `[]`.
const LISTINGS_API = "https://cms.buxton.com.au/api/v1/listings/";
const PAGE_SIZE = 50;

export function buildSearchUrl(suburb: SuburbInput, _config: SoldOnlyAdapterConfig): string {
  const params = new URLSearchParams({
    status: "sold",
    suburb: suburb.locality,
    postcode: suburb.postcode,
    limit: String(PAGE_SIZE),
    offset: "0",
  });
  return `${LISTINGS_API}?${params}`;
}

export function buildPaginatedUrl(searchUrl: string, page: number): string {
  const url = new URL(searchUrl);
  url.searchParams.set("offset", String((Math.max(1, page) - 1) * PAGE_SIZE));
  return url.toString();
}

interface BuxtonListing {
  external_id?: string | number | null;
  street_address?: string | null;
  suburb?: string | null;
  postcode?: string | null;
  address_state?: string | null;
  status?: string | null;
  listing_payload?: {
    sold_details_sold_date?: string | null;
    sold_details_sold_price?: string | null;
    sold_details_sold_price_display?: boolean | null;
    category?: string | null;
  } | null;
}

/** The site's canonical property page: /property/<external id>/<street-suburb-state-postcode>/. */
export function buildBuxtonPropertyUrl(listing: BuxtonListing): string | null {
  const id = listing.external_id;
  const street = listing.street_address?.trim();
  const suburb = listing.suburb?.trim();
  if (!id || !street || !suburb || !listing.postcode) return null;
  const state = (listing.address_state ?? "vic").toLowerCase();
  return `https://buxton.com.au/property/${id}/${slugify(`${street} ${suburb} ${state} ${listing.postcode}`)}/`;
}

/** Discovery cards from one listings-API page; null when the body is not that API's JSON array. */
export function parseBuxtonDiscoveryPreviews(body: string, _pageUrl: string): DiscoveryPreview[] | null {
  let listings: unknown;
  try {
    listings = JSON.parse(body);
  } catch {
    return null;
  }
  if (!Array.isArray(listings)) return null;
  const cards: DiscoveryPreview[] = [];
  for (const listing of listings as BuxtonListing[]) {
    const url = buildBuxtonPropertyUrl(listing);
    if (!url || listing.status !== "sold") continue;
    const sold = listing.listing_payload ?? {};
    cards.push({
      url,
      address: `${listing.street_address}, ${listing.suburb} ${listing.postcode}`,
      suburb: listing.suburb ?? undefined,
      postcode: listing.postcode ?? undefined,
      state: listing.address_state?.toUpperCase() ?? undefined,
      saleDate: sold.sold_details_sold_date ?? undefined,
      propertyType: sold.category ?? undefined,
    });
  }
  return cards;
}
